import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';
import * as FiIcons from 'react-icons/fi';

const { FiHome, FiUserPlus, FiSettings, FiMenu, FiX } = FiIcons;

const Header = () => {
  const location = useLocation();
  const { t, lang, dir } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  const navItems = [
    { path: '/', icon: FiHome, label: t('nav.map') },
    { path: '/register', icon: FiUserPlus, label: t('nav.register') },
    { path: '/admin', icon: FiSettings, label: t('nav.admin') },
  ];

  return (
    <motion.header initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm w-full">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-4 group rtl:space-x-reverse relative z-[110]">
            <Logo />
            <div className="flex flex-col">
              <h1 className="text-xl font-black tracking-tight text-slate-900 leading-none group-hover:text-green-600 transition-colors">
                MINECRAFT <span className="text-blue-600">EDU</span>
              </h1>
              <div className="flex items-center space-x-1 mt-1 rtl:space-x-reverse">
                <span className="h-px w-3 bg-green-500"></span>
                <p className="text-[10px] text-slate-500 font-bold tracking-[0.15em] uppercase">
                  {lang === 'ar' ? 'شبكة الأردن' : 'JORDAN NETWORK'}
                </p>
              </div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center space-x-1 rtl:space-x-reverse">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} className={`flex items-center space-x-2 rtl:space-x-reverse px-4 py-2.5 rounded-xl transition-all duration-300 font-bold text-sm ${location.pathname === item.path ? 'bg-slate-50 text-green-700 border border-green-100' : 'text-slate-500 hover:text-green-600 hover:bg-slate-50'}`}>
                <SafeIcon icon={item.icon} className="text-lg" />
                <span>{item.label}</span>
              </Link>
            ))}
            <div className="ml-4 rtl:ml-0 rtl:mr-4 border-l rtl:border-l-0 rtl:border-r border-slate-100 pl-4 rtl:pl-0 rtl:pr-4">
              <LanguageSwitcher />
            </div>
          </nav>

          <div className="md:hidden flex items-center space-x-2 rtl:space-x-reverse relative z-[110]">
            {!isMenuOpen && <LanguageSwitcher />}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2.5 rounded-xl bg-slate-50 text-slate-600 border border-slate-100">
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-xl" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsMenuOpen(false)} className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] z-[90] md:hidden" />
            <motion.div initial={{ x: dir === 'rtl' ? '100%' : '-100%' }} animate={{ x: 0 }} exit={{ x: dir === 'rtl' ? '100%' : '-100%' }} className={`fixed top-0 bottom-0 ${dir === 'rtl' ? 'right-0' : 'left-0'} w-[85%] max-w-[320px] bg-white z-[100] md:hidden flex flex-col pt-24 px-6 shadow-2xl`}>
              <div className="flex flex-col space-y-3">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{lang === 'ar' ? 'القائمة الرئيسية' : 'Main Menu'}</p>
                {navItems.map((item) => (
                  <Link key={item.path} to={item.path} className={`flex items-center space-x-4 rtl:space-x-reverse p-4 rounded-2xl font-bold text-lg ${location.pathname === item.path ? 'bg-green-600 text-white shadow-lg' : 'text-slate-600 hover:bg-slate-50'}`}>
                    <SafeIcon icon={item.icon} className="text-xl" />
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;